import React, { useCallback, useMemo } from 'react';
import Animated from 'react-native-reanimated';
import LinearGradient from 'utils/packages/linearGradient';
import { FlashList } from '@shopify/flash-list';
import { SettingsItem } from 'design-system/SettingsItem';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { rem } from 'design-system/values';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import useColorScheme from 'hooks/useColorScheme';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { selectAnalyticsCategoriesMap, selectMonthlyTotals, selectMonthlyTotalsFilteredWithFallbackMonths, } from 'features/analytics/selectors';
import { useOpenCategory } from 'features/analytics/hooks/useOpenCategory';
import { IconEdit } from 'design-system';
import { sortMonthlyTotals } from 'features/budgeting/selectors';
import { isWeb } from '../../../../constants';
import RowCategory from '../row/RowCategory';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => String(item.id);
const SubCategoriesList = ({ categoryId, selectedIndex, }) => {
    const categoriesMap = useAppSelector((store) => selectAnalyticsCategoriesMap(store, selectedIndex));
    const data = useMemo(() => {
        if (!categoryId)
            return undefined;
        const subcategories = categoriesMap?.parentCategories[categoryId]?.subCategoryIds
            ?.map((subCategoryId) => ({
            ...categoriesMap?.subCategories[subCategoryId],
            total: categoriesMap?.subCategories[subCategoryId]?.totalWithExcluded,
        }))
            ?.filter((item) => !!item.transactionsCount);
        return subcategories ? sortMonthlyTotals(subcategories) : undefined;
    }, [
        categoriesMap?.parentCategories,
        categoriesMap?.subCategories,
        categoryId,
    ]);
    const parentCategory = categoriesMap?.parentCategories[categoryId];
    const totalSpendingOrIncome = useMemo(() => {
        if (!data)
            return 0;
        const subsTotal = data?.reduce((acc, curr) => acc + curr.totalWithExcluded, 0);
        const onlyParentTotal = (parentCategory?.totalWithExcluded ?? 0) - (subsTotal ?? 0);
        if ((parentCategory?.totalWithExcluded ?? 0) > 0) {
            return data?.reduce((acc, curr) => acc + (curr.totalWithExcluded > 0 ? curr.totalWithExcluded : 0), onlyParentTotal > 0 ? onlyParentTotal : 0);
        }
        return data?.reduce((acc, curr) => acc + (curr.totalWithExcluded < 0 ? curr.totalWithExcluded : 0), onlyParentTotal > 0 ? 0 : onlyParentTotal);
    }, [data, parentCategory]);
    const styles = useStyles(styleSet);
    const unfilteredMonthlyTotals = useAppSelector(selectMonthlyTotals);
    const filteredMonthlyTotals = useAppSelector(selectMonthlyTotalsFilteredWithFallbackMonths);
    const monthlyTotals = filteredMonthlyTotals?.results || unfilteredMonthlyTotals;
    const openCategory = useOpenCategory(monthlyTotals, selectedIndex, 'Category', filteredMonthlyTotals?.step, filteredMonthlyTotals?.accountIds, 'push');
    const renderItem = useCallback(({ item, index }) => {
        const isSameSignAsParentTotal = (totalSpendingOrIncome < 0 && item.totalWithExcluded < 0) ||
            (totalSpendingOrIncome > 0 && item.totalWithExcluded > 0);
        return (<FlatListItemCard index={index + 1} itemsLength={data.length + 1} style={styles.listItem}>
          <RowCategory category={item} totalSpending={totalSpendingOrIncome} onPress={openCategory} selectable={false} showExpensePercentage={isSameSignAsParentTotal}/>
        </FlatListItemCard>);
    }, [data, openCategory, styles.listItem, totalSpendingOrIncome]);
    const colors = useColors();
    const navigation = useMainStackNavigation();
    const ListHeaderComponent = useMemo(() => {
        const onPress = () => navigation.navigateDeprecated('ManageCategories', {
            initialTab: 'subcategories',
        });
        const content = (<SettingsItem title="Manage subcategories" style={!data?.length && styles.addButton} disabled={!data?.length} onPress={onPress} renderIcon={() => (<LinearGradient colors={[colors.background.dark, colors.background.dark]} style={styles.addIcon}>
            <IconEdit color={colors.elements.brand}/>
          </LinearGradient>)}/>);
        if (!data?.length) {
            return (<TouchableBounceCard onDark onPress={onPress} noMarginBottom>
          {content}
        </TouchableBounceCard>);
        }
        return (<FlatListItemCard index={0} itemsLength={data.length + 1} style={[styles.listItem, styles.addContainer]}>
        {content}
      </FlatListItemCard>);
    }, [
        colors.background.dark,
        colors.elements.brand,
        data?.length,
        styles.addButton,
        styles.addContainer,
        styles.addIcon,
        styles.listItem,
    ]);
    const colorScheme = useColorScheme();
    const baseProps = {
        scrollEnabled: false,
        data,
        renderItem,
        keyExtractor,
        ListHeaderComponent,
    };
    if (isWeb) {
        return <Animated.FlatList {...baseProps}/>;
    }
    return <AnimatedFlashList {...baseProps} extraData={colorScheme}/>;
};
const styleSet = createStyleSheets((colors) => ({
    listItem: {
        backgroundColor: colors.cards.onDark,
    },
    addIcon: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(44),
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(16),
    },
    addButton: {
        marginHorizontal: rem(16),
    },
    addContainer: {
        marginTop: rem(16),
    },
}));
export default SubCategoriesList;
